import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { excludeCollaborators } from '../../../utils/userUtils';
import {
  QDatepicker,
  QFormControl,
  QInput,
  QMultiSelect,
  QSelect,
  QSelectItem,
  QStack,
  QTextarea,
  SearchResource,
  useCurrentUser,
} from '@qualio/ui-components';

import { quickSearchApiFactory } from '../../../api/quickSearch.api';
import { useUsersOptions } from '../../../hooks/useUsersOptions';
import {
  EventTag,
  EventTemplateField,
  EventType,
  ProbabilityType,
  Product,
  QualityEvent,
  QualityEventCreate,
  RiskType,
  RootCause,
  SeverityType,
  User,
} from '../../../types';
import { Relation } from '../../../types/relation';
import { eventFieldsSetter, registryItemLabel } from '../../../utils/eventUtils';
import EventFormOptionalFields from './EventFormOptionalFields';
import MultiSelectWithSuggestions, { ResourceSelectItem } from './MultiSelectWithSuggestions';
import { DateTime } from 'luxon';
import { formatDatePickerComponentDateToString } from '../../../utils/datetimeUtils';
import { useFlags } from '../../../external/LaunchDarklyAdapter';
import { nativeCodeNameFilterOption } from '../../EventTable/components/codeNameFilterOption';
import TagItemLabel from '../../EventTable/components/TagItemLabel';
import { tagFilterOption } from '../../EventTable/components/tagFilterOption';

type Props = {
  formMethods: UseFormReturn<QualityEventCreate>;
  onSubmit: any;
  data: {
    eventTypes: EventType[];
    eventTags: EventTag[];
    probabilities: ProbabilityType[];
    severities: SeverityType[];
    risks: RiskType[];
    users: User[];
    products: Product[];
    rootCauses: RootCause[];
  };
  event?: QualityEvent;
  related?: Relation[];
  suppliers?: QSelectItem[];
  relatedData?: {
    relatedDocuments: ResourceSelectItem[];
    relatedEvents: ResourceSelectItem[];
  };
  optionalFields?: EventTemplateField[] | null;
  isDueDateEditable?: boolean;
};

const EventForm: React.FC<Props> = ({
  formMethods,
  onSubmit,
  data,
  event,
  related,
  suppliers,
  optionalFields,
  relatedData,
  isDueDateEditable = true,
}) => {
  const { t } = useTranslation();
  const isMultiSelectEventsListEnabled = useFlags('multiSelectEventsList');
  let defaultRelated: QSelectItem[] = [];
  let defaultSourceEvents: ResourceSelectItem[] = [];
  let defaultEscalatedTo: ResourceSelectItem[] = [];
  const isCreateFlow = !event;
  const isEditFlow = !!event;

  const documentsSuggestionApi = quickSearchApiFactory('document');
  const eventsSuggestionApi = quickSearchApiFactory('quality-event');
  const optionalFieldsRender = optionalFields ?? [
    { attribute_name: 'product', mandatory: false },
    { attribute_name: 'root_cause', mandatory: false },
    { attribute_name: 'risk', mandatory: true },
  ];

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = formMethods;

  let eventTypesOptions: QSelectItem<React.ReactNode>[] = [];
  const { userId } = useCurrentUser();

  if (event) {
    eventTypesOptions = [{ value: String(event.workflow_id), label: event.workflow }];
  } else {
    eventTypesOptions = data.eventTypes
      .filter((eventType) => eventType.active && eventType.status === 'effective')
      .map((eventType) => ({
        value: String(eventType.id),
        label: eventType.name,
        code: isMultiSelectEventsListEnabled ? eventType.prefix : undefined,
      }));
  }

  const eventTypeTimeLimit = Object.assign(
    {},
    ...data.eventTypes.map((x) => ({ [String(x.id)]: String(x.defaultTimeLimit) })),
  );

  const eventTypeDefaultOwnerIdMap = Object.assign(
    {},
    ...data.eventTypes.map((x) => ({ [String(x.id)]: x.defaultOwnerId })),
  );

  const ownerCandidates = useMemo(
    () => (event ? excludeCollaborators(data.users, event.collaborators) : data.users),
    [data.users, event],
  );
  const usersOptions = useUsersOptions(ownerCandidates, 'can_work_on_issue');

  useEffect(() => {
    setValue('owner_id', String(userId));
  }, [userId, setValue]);

  const eventTagsOptions = data.eventTags.map((tag) => ({
    value: String(tag.id),
    label: isMultiSelectEventsListEnabled ? <TagItemLabel tag={tag} /> : tag.name,
  }));

  const probabilitiesOptions = data.probabilities.map((probability) => ({
    value: probability.id,
    label: probability.name,
  }));

  const severitiesOptions = data.severities.map((severity) => ({
    value: severity.id,
    label: severity.name,
  }));

  const risksOptions = data.risks.map((risk) => ({
    value: risk.id,
    label: risk.name,
  }));

  const productsOptions: QSelectItem<string, string>[] = data.products.map((product) => ({
    value: String(product.id),
    label: registryItemLabel(product),
    code: isMultiSelectEventsListEnabled ? product.displayId : undefined,
  }));

  const rootCausesOptions: QSelectItem<string, string>[] = data.rootCauses.map((rootCause) => ({
    value: String(rootCause.id),
    label: registryItemLabel(rootCause),
    code: isMultiSelectEventsListEnabled ? rootCause.displayId : undefined,
  }));

  if (related) {
    const filteredRelated = related.filter((item) => item.relation_direction === 'left');
    defaultRelated = filteredRelated.map((item) => ({
      value: item.uuid,
      label: item.label,
    }));
  }
  if (event?.escalated_from) {
    defaultSourceEvents = event.escalated_from.map((item) => ({
      value: String(item.id),
      label: `${item.code} ${item.title}`,
      code: item.code,
      title: item.title,
      resource: 'quality-event' as SearchResource,
    }));
  }
  if (event?.escalated_to) {
    defaultEscalatedTo = event.escalated_to.map((item) => ({
      value: String(item.id),
      label: `${item.code} ${item.title}`,
      code: item.code,
      title: item.title,
      resource: 'quality-event' as SearchResource,
    }));
  }

  const optionalfieldSelectOptions = {
    probabilitiesOptions,
    severitiesOptions,
    risksOptions,
    productsOptions,
    defaultRelated,
    defaultSupplier: suppliers ?? [],
    rootCausesOptions,
  };
  useEffect(() => {
    eventFieldsSetter(event, setValue);
  }, [event, setValue]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QStack direction="column" spacing={4}>
          {!isEditFlow && (
            <QFormControl
              label={t('common.labels.eventType')}
              error={errors.workflow_id && errors.workflow_id.message}
              isInvalid={!!errors.workflow_id}
              isRequired
              data-metrics="event-type-select"
            >
              <Controller
                control={control}
                name="workflow_id"
                render={({ field: { ref: _ref, onChange, ...field } }) => (
                  <QSelect
                    {...field}
                    onChange={(changeObject) => {
                      if (!changeObject) {
                        return;
                      }

                      if (!event) {
                        setValue(
                          'due_date',
                          formatDatePickerComponentDateToString(
                            DateTime.now().plus({ days: eventTypeTimeLimit[changeObject.value] }).toJSDate(),
                          ),
                        );
                        const ownerIdToSet = eventTypeDefaultOwnerIdMap[changeObject.value];
                        if (ownerIdToSet && usersOptions.some((option) => option.value === String(ownerIdToSet))) {
                          setValue('owner_id', String(ownerIdToSet));
                        } else {
                          setValue('owner_id', String(userId));
                        }
                      }
                      onChange(changeObject.value);
                    }}
                    options={eventTypesOptions}
                    menuPosition="fixed"
                    filterOption={
                      isMultiSelectEventsListEnabled
                        ? (option, searchText) =>
                            option.data.value !== field.value && nativeCodeNameFilterOption(option, searchText)
                        : undefined
                    }
                  />
                )}
              />
            </QFormControl>
          )}
          <QFormControl
            label={t('common.labels.title')}
            error={errors.title && errors.title.message}
            isInvalid={!!errors.title}
            data-metrics="event-title-input"
            isRequired
          >
            <Controller
              control={control}
              name="title"
              render={({ field: { ref: _ref, ...field } }) => <QInput {...field} value={field.value ?? ''} />}
            />
          </QFormControl>
          <QFormControl
            label={t('common.labels.owner')}
            error={errors.owner_id && errors.owner_id.message}
            isInvalid={!!errors.owner_id}
            data-metrics="event-owner-select"
            isRequired
          >
            <Controller
              control={control}
              name="owner_id"
              render={({ field: { ref: _ref, onChange, ...field } }) => (
                <QSelect
                  {...field}
                  onChange={(changeObject) => {
                    if (changeObject) {
                      onChange(changeObject.value);
                    }
                  }}
                  options={usersOptions}
                  menuPosition="fixed"
                />
              )}
            />
          </QFormControl>
          {isDueDateEditable && (
            <QFormControl
              label={t('common.labels.dueDate')}
              isRequired
              isInvalid={Boolean(errors.due_date)}
              error={errors.due_date?.message}
            >
              <Controller
                control={control}
                name="due_date"
                render={({ field: { value, onChange } }) => (
                  <QDatepicker
                    value={value || undefined}
                    onChange={(date) => onChange(date ? formatDatePickerComponentDateToString(date) : date)}
                    minDate={
                      event
                        ? DateTime.fromSeconds(event.created_at).startOf('day').toJSDate()
                        : DateTime.now().toJSDate()
                    }
                  />
                )}
              />
            </QFormControl>
          )}
          {!isCreateFlow && (
            <>
              <QFormControl label={t('common.labels.description')} data-metrics="event-description-input">
                <Controller
                  control={control}
                  name="description"
                  render={({ field: { ref: _ref, ...field } }) => (
                    <QTextarea {...field} data-cy="event-description-input-textarea" />
                  )}
                />
              </QFormControl>
              <QFormControl label={t('common.labels.tags')} data-metrics="event-tags-select">
                <Controller
                  control={control}
                  name="tags_ids"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <QMultiSelect
                      {...field}
                      onChange={(val) => onChange(val.map((c) => c.value))}
                      options={eventTagsOptions}
                      filterOption={isMultiSelectEventsListEnabled ? tagFilterOption : undefined}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('events.labels.relatedEvents')}
                data-metrics="event-related-event-select"
                isRequired={false}
              >
                <Controller
                  control={control}
                  name="related_events"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <MultiSelectWithSuggestions
                      {...field}
                      suggestionApi={eventsSuggestionApi}
                      defaults={relatedData?.relatedEvents}
                      valuePropFromSuggestion="qri"
                      domain="events"
                      renderResourceLabel={isMultiSelectEventsListEnabled}
                      omitFromSuggestionResults={event ? [event.id] : []}
                      onChange={(
                        items: {
                          value: any;
                        }[],
                      ) => onChange(items.map((item: { value: any }) => item.value))}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('events.labels.relatedDocuments')}
                data-metrics="event-related-document-select"
                isRequired={false}
              >
                <Controller
                  control={control}
                  name="related_documents"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <MultiSelectWithSuggestions
                      {...field}
                      suggestionApi={documentsSuggestionApi}
                      defaults={relatedData?.relatedDocuments}
                      valuePropFromSuggestion="qri"
                      domain="documents"
                      renderResourceLabel={isMultiSelectEventsListEnabled}
                      onChange={(
                        items: {
                          value: any;
                        }[],
                      ) => onChange(items.map((item: { value: any }) => item.value))}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('events.labels.sourceEvent')}
                data-metrics="event-source-event-select"
                isRequired={false}
              >
                <Controller
                  control={control}
                  name="escalated_from"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <MultiSelectWithSuggestions
                      {...field}
                      suggestionApi={eventsSuggestionApi}
                      defaults={defaultSourceEvents}
                      valuePropFromSuggestion="id"
                      domain="events"
                      renderResourceLabel={isMultiSelectEventsListEnabled}
                      omitFromSuggestionResults={event ? [event.id] : []}
                      onChange={(
                        items: {
                          value: any;
                        }[],
                      ) => onChange(items.map((item: { value: any }) => item.value))}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('events.labels.escalatedTo')}
                data-metrics="event-escalated-to-select"
                isRequired={false}
              >
                <Controller
                  control={control}
                  name="escalated_to"
                  render={({ field: { ref: _ref, onChange, ...field } }) => (
                    <MultiSelectWithSuggestions
                      {...field}
                      suggestionApi={eventsSuggestionApi}
                      defaults={defaultEscalatedTo}
                      valuePropFromSuggestion="id"
                      domain="events"
                      renderResourceLabel={isMultiSelectEventsListEnabled}
                      omitFromSuggestionResults={event ? [event.id] : []}
                      onChange={(
                        items: {
                          value: any;
                        }[],
                      ) => onChange(items.map((item: { value: any }) => item.value))}
                    />
                  )}
                />
              </QFormControl>
              {optionalFieldsRender?.map((field) => {
                if (!field.workflow_step_id) {
                  return (
                    <EventFormOptionalFields
                      fieldName={field.attribute_name}
                      required={field.mandatory}
                      formMethods={formMethods}
                      selectOptionProps={optionalfieldSelectOptions}
                      key={field.attribute_name}
                    />
                  );
                }
                return null;
              })}
            </>
          )}
        </QStack>
      </form>
    </FormProvider>
  );
};

export default EventForm;
